import React, { createContext, ReactNode, useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { toast, type ToastOptions } from "react-toastify";
import type { ResponseNotifyResult } from "../../types/api";

/* ------------------------------------------------------------------ */
/* Visual variants                                                     */
/* ------------------------------------------------------------------ */
export type NotifyVariant = "success" | "error" | "warning" | "info";

/* ------------------------------------------------------------------ */
/* Default code → variant mapping                                       */
/* ------------------------------------------------------------------ */
const DEFAULT_CODE_VARIANT_MAP: Record<number, NotifyVariant> = {
  400: "warning",
  401: "error",
  403: "error",
  404: "error",
  406: "error",
  422: "warning",
};

/* ------------------------------------------------------------------ */
/* Notify channels                                                     */
/* ------------------------------------------------------------------ */
export const NotifyChannel = {
  Toast: "toast" as const,
  Modal: "modal" as const,
  Popup: "popup" as const,
  Silent: "silent" as const,
};
export type NotifyChannelType = typeof NotifyChannel[keyof typeof NotifyChannel];

/* ------------------------------------------------------------------ */
/* Notify options                                                       */
/* ------------------------------------------------------------------ */
export interface NotifyDisplayOptions {
  channel?: NotifyChannelType;
  channelOptions?: ToastOptions | ModalOptions | PopupOptions | null;
  codeVariantMap?: Record<number, NotifyVariant>;
}

export interface ModalOptions {
  children?: ReactNode | null;
}

export interface PopupOptions {
  duration?: number;
}

/* ------------------------------------------------------------------ */
/* Global context for modal & popup                                     */
/* ------------------------------------------------------------------ */
interface NotifyContextType {
  showModal: (message: string, variant?: NotifyVariant, options?: ModalOptions) => void;
  showPopup: (message: string, variant?: NotifyVariant, options?: PopupOptions) => void;
}

const NotifyContext = createContext<NotifyContextType | null>(null);
let globalNotify: NotifyContextType | null = null;

/* ------------------------------------------------------------------ */
/* NotifyProvider - wrap your app                                        */
/* ------------------------------------------------------------------ */
export const NotifyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalVariant, setModalVariant] = useState<NotifyVariant>("info");
  const [modalOptions, setModalOptions] = useState<ModalOptions>();

  // Popup state
  const [popupMessage, setPopupMessage] = useState("");
  const [popupVariant, setPopupVariant] = useState<NotifyVariant>("info");
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupOptions, setPopupOptions] = useState<PopupOptions>();

  /* ---------------------------------------------------------------- */
  /* Global controller setup                                           */
  /* ---------------------------------------------------------------- */
  const showModal = (message: string, variant: NotifyVariant = "info", options?: ModalOptions) => {
    setModalMessage(message);
    setModalVariant(variant);
    setModalOptions(options);
    setModalOpen(true);
  };

  const showPopup = (message: string, variant: NotifyVariant = "info", options?: PopupOptions) => {
    setPopupMessage(message);
    setPopupVariant(variant);
    setPopupOptions(options);
    setPopupVisible(true);

    if (options?.duration) {
      setTimeout(() => setPopupVisible(false), options.duration);
    } else {
      setTimeout(() => setPopupVisible(false), 3000);
    }
  };

  useEffect(() => {
    globalNotify = { showModal, showPopup };
    return () => { globalNotify = null; };
  }, []);

  const closeModal = () => setModalOpen(false);

  return (
    <NotifyContext.Provider value={{ showModal, showPopup }}>
      {children}

      {/* Modal */}
      <Dialog open={modalOpen} onClose={closeModal}>
        <DialogTitle>
          <Typography color={getVariantColor(modalVariant)}>{modalVariant.toUpperCase()}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{modalMessage}</Typography>
          {modalOptions?.children}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Popup */}
      {popupVisible && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            right: 20,
            padding: 16,
            backgroundColor: getVariantColor(popupVariant),
            color: "white",
            borderRadius: 8,
            zIndex: 9999,
          }}
        >
          {popupMessage}
        </div>
      )}
    </NotifyContext.Provider>
  );
};

/* ------------------------------------------------------------------ */
/* Variant color helper                                                 */
/* ------------------------------------------------------------------ */
const getVariantColor = (variant: NotifyVariant) => {
  switch (variant) {
    case "success": return "green";
    case "warning": return "orange";
    case "error": return "red";
    case "info": 
    default: return "blue";
  }
};

/* ------------------------------------------------------------------ */
/* Global notify functions                                              */
/* ------------------------------------------------------------------ */
export const displayModal = (message: string, variant: NotifyVariant = "info", options?: ModalOptions) => {
  if (!globalNotify) {
    console.warn("[Modal] NotifyProvider is not mounted");
    return;
  }
  globalNotify.showModal(message, variant, options);
};

export const displayPopup = (message: string, variant: NotifyVariant = "info", options?: PopupOptions) => {
  if (!globalNotify) {
    console.warn("[Popup] NotifyProvider is not mounted");
    return;
  }
  globalNotify.showPopup(message, variant, options);
};

/* ------------------------------------------------------------------ */
/* Toast helper                                                         */
/* ------------------------------------------------------------------ */
const calculateDuration = (message: string) => {
  if(message?.length && message?.length > 0) {
    return Math.min(Math.max(message.length * 50, message.length < 20 ? 2000 : 3200), 15000)
  }
  return 0;
};

const displayToast = (message: string, variant: NotifyVariant, options?: ToastOptions | null) => {
  const baseOptions: ToastOptions = { autoClose: calculateDuration(message), ...options };
  switch (variant) {
    case "success": toast.success(message, baseOptions); break;
    case "warning": toast.warning(message, baseOptions); break;
    case "info": toast.info(message, baseOptions); break;
    case "error":
    default: toast.error(message, baseOptions); break;
  }
};

/* ------------------------------------------------------------------ */
/* notifyDisplay entry point                                            */
/* ------------------------------------------------------------------ */
export const notifyDisplay = (result: ResponseNotifyResult, options: NotifyDisplayOptions = {}) => {
  const { channel = NotifyChannel.Toast, channelOptions, codeVariantMap } = options;
  if (channel === NotifyChannel.Silent) return;

  const finalCodeVariantMap = { ...DEFAULT_CODE_VARIANT_MAP, ...codeVariantMap };
  const variant = result.code && finalCodeVariantMap[result.code] ? finalCodeVariantMap[result.code] : result.success ? "success" : "error";

  switch (channel) {
    case NotifyChannel.Toast:
      displayToast(result.message, variant, channelOptions as ToastOptions);
      break;
    case NotifyChannel.Modal:
      displayModal(result.message, variant, channelOptions as ModalOptions);
      break;
    case NotifyChannel.Popup:
      displayPopup(result.message, variant, channelOptions as PopupOptions);
      break;
  }
};
